import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { authApi } from '../api/endpoints.js';
import { ApiError, setAccessToken } from '../api/client.js';

export default function ChangePasswordPage() {
    const { user, refreshUser, logout } = useAuth();
    const navigate = useNavigate();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const forced = !!user?.must_change_password;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setFieldErrors({});

        // Client-side validation
        if (newPassword !== confirmPassword) {
            setFieldErrors({ confirm: 'Passwords do not match.' });
            return;
        }
        if (newPassword === currentPassword) {
            setFieldErrors({ new: 'New password must differ from your current password.' });
            return;
        }
        const issues = checkPasswordRules(newPassword);
        if (issues.length > 0) {
            setFieldErrors({ new: issues.join(' ') });
            return;
        }

        setSubmitting(true);
        try {
            await authApi.changePassword(currentPassword, newPassword);
            // change-password bumps token_version on the server, which
            // invalidates the access token we currently have. Manually call
            // /auth/refresh (using the still-valid refresh cookie) to get a
            // fresh access token before doing anything else.
            const refreshRes = await fetch('/api/v1/auth/refresh', {
                method: 'POST',
                credentials: 'include',
            });
            if (refreshRes.ok) {
                const refreshData = await refreshRes.json();
                setAccessToken(refreshData.token);
            }
            // Refresh cached user (now must_change_password=false)
            const me = await refreshUser();
            const home = me?.role === 'admin' ? '/admin' : '/';
            navigate(home, { replace: true });
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'INVALID_CREDENTIALS') {
                    setFieldErrors({ current: 'Current password is incorrect.' });
                } else if (err.code === 'PASSWORD_INVALID') {
                    const failed = err.details?.rules_failed || [];
                    setFieldErrors({ new: passwordErrorMessage(failed) });
                } else if (err.code === 'PASSWORD_REUSED') {
                    setFieldErrors({ new: 'You have used this password recently. Please choose a different one.' });
                } else {
                    setError(err.message);
                }
            } else {
                setError('Could not change password. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <div className="flex-1 flex items-center justify-center px-4">
                <div className="w-full max-w-sm">
                    {/* Branding */}
                    <div className="text-center" style={{ marginBottom: 24 }}>
                        <div
                            className="inline-flex items-center justify-center"
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: 12,
                                background: 'var(--color-status-complete)',
                                color: '#fff',
                                fontSize: 18,
                                fontWeight: 600,
                                marginBottom: 14,
                            }}
                        >
                            S
                        </div>
                        <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
                            {forced ? 'Set a new password' : 'Change your password'}
                        </h1>
                        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                            {forced
                                ? 'Welcome to Stars APT. Please choose a new password before continuing.'
                                : 'Update the password for your account.'}
                        </p>
                    </div>

                    {/* Card */}
                    <div className="surface-card" style={{ padding: 24 }}>
                        <form onSubmit={onSubmit} className="flex flex-col" style={{ gap: 14 }}>
                            <Field
                                id="current"
                                label={forced ? 'Temporary password' : 'Current password'}
                                value={currentPassword}
                                onChange={setCurrentPassword}
                                error={fieldErrors.current}
                                autoFocus
                                autoComplete="current-password"
                            />
                            <Field
                                id="new"
                                label="New password"
                                value={newPassword}
                                onChange={setNewPassword}
                                error={fieldErrors.new}
                                autoComplete="new-password"
                                helper="At least 10 characters and 1 special character. Must differ from your current password."
                            />
                            <Field
                                id="confirm"
                                label="Confirm new password"
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                error={fieldErrors.confirm}
                                autoComplete="new-password"
                            />

                            <PasswordStrengthIndicator value={newPassword} />

                            {error && (
                                <div
                                    style={{
                                        background: 'var(--color-pill-rejected-bg)',
                                        border: '0.5px solid var(--color-danger-border)',
                                        color: 'var(--color-danger)',
                                        borderRadius: 'var(--border-radius-md)',
                                        padding: '8px 12px',
                                        fontSize: 13,
                                    }}
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn btn-primary"
                                style={{ width: '100%', padding: 10, marginTop: 4 }}
                            >
                                {submitting ? 'Saving…' : (forced ? 'Set password and continue' : 'Change password')}
                            </button>
                        </form>
                    </div>

                    {/* Sign-out option (only meaningful if forced — gives user a way out
                        if they used the wrong account) */}
                    {forced && (
                        <p style={{ textAlign: 'center', marginTop: 14 }}>
                            <button
                                type="button"
                                onClick={logout}
                                style={{
                                    fontSize: 12,
                                    color: 'var(--color-text-tertiary)',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                }}
                            >
                                Sign out instead
                            </button>
                        </p>
                    )}
                </div>
            </div>

            <footer
                style={{
                    textAlign: 'center',
                    fontSize: 11,
                    color: 'var(--color-text-tertiary)',
                    padding: '20px 16px',
                    borderTop: '0.5px solid var(--color-border-tertiary)',
                }}
            >
                Stars Attendance &amp; Productivity Tracking
            </footer>
        </div>
    );
}

function Field({ id, label, value, onChange, error, helper, autoFocus, autoComplete }) {
    return (
        <div>
            <label
                htmlFor={id}
                style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 6,
                }}
            >
                {label}
            </label>
            <input
                id={id}
                type="password"
                required
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="input"
                autoFocus={autoFocus}
                autoComplete={autoComplete}
            />
            {helper && !error && (
                <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4, lineHeight: 1.5 }}>
                    {helper}
                </p>
            )}
            {error && (
                <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>
            )}
        </div>
    );
}

function PasswordStrengthIndicator({ value }) {
    if (!value) return null;
    const checks = [
        { label: 'At least 10 characters', ok: value.length >= 10 },
        { label: 'Contains a special character', ok: /[^A-Za-z0-9]/.test(value) },
    ];
    return (
        <div
            style={{
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--border-radius-md)',
                padding: 10,
                fontSize: 12,
            }}
        >
            {checks.map((c) => (
                <div key={c.label} className="flex items-center" style={{ gap: 6, marginBottom: 2 }}>
                    <span
                        style={{
                            width: 14,
                            height: 14,
                            borderRadius: 7,
                            background: c.ok ? 'var(--color-status-complete)' : 'var(--color-border-tertiary)',
                            color: '#fff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 9,
                            flexShrink: 0,
                        }}
                    >
                        {c.ok ? '✓' : ''}
                    </span>
                    <span style={{ color: c.ok ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                        {c.label}
                    </span>
                </div>
            ))}
        </div>
    );
}

function checkPasswordRules(pwd) {
    const issues = [];
    if (pwd.length < 10) issues.push('Must be at least 10 characters.');
    if (!/[^A-Za-z0-9]/.test(pwd)) issues.push('Must contain at least 1 special character.');
    return issues;
}

function passwordErrorMessage(failed) {
    const parts = [];
    if (failed.includes('min_length')) parts.push('must be at least 10 characters');
    if (failed.includes('special_char')) parts.push('must contain at least 1 special character');
    if (failed.includes('blocklisted')) parts.push('is too common — pick something less guessable');
    if (parts.length === 0) return 'Does not meet password requirements.';
    return 'Password ' + parts.join(', ') + '.';
}
